import { Schema } from '@mcn-platform/models';

interface BuildFilterParams {
  store: any;
  modelName: string;
  filters: string[];
  searchText?: string;
  searchLibraries?: string[];
  searchMemberships?: string[];
  searchPastFuture?: string;
  activeStatusOnly?: boolean;
  filter?: any;
}

interface BuildFilterResult {
  combinedFilter: {
    and: any[];
    organizationID?: string;
    libraryIds?: string[];
    combineOrganizationWithLibraries?: boolean;
  };
  sortValues: { field: string; direction: 'asc' | 'desc' };
  crossOrganization: boolean;
}

export function buildCombinedFilter(params: BuildFilterParams): BuildFilterResult {
  const {
    store,
    modelName,
    filters,
    searchText,
    searchLibraries = [],
    searchMemberships = [],
    searchPastFuture,
    activeStatusOnly,
    filter,
  } = params;

  const combinedFilter: { and: any[] } = { and: [] };
  let crossOrganization = false;
  let sortValues = Schema.getSortValues(modelName);

  if (filters.includes('searchText') && searchText?.length) {
    const searchableList = Schema.getSearchablePropertyList(modelName);
    combinedFilter.and.push({
      or: searchableList.map((field: string) => ({
        [field]: { matchPhrasePrefix: searchText.toLowerCase() },
      })),
    });
  }

  if (filters.includes('library') || filters.includes('combinedLibrary') || filters.includes('portalLibraries')) {
    if (searchLibraries.length > 0) {
      // User selected libraries: filter by those only
      combinedFilter.and.push({
        or: searchLibraries.map((id: string) => ({ libraries: { matchPhrase: id } })),
      });
      crossOrganization = true;
    } else if (filters.includes('combinedLibrary')) {
      // No selection: current org OR libraries shared with this org
      const orgId = store.currentOrganization?.id ?? '';
      const sharedLibraryIds = (store.receivedLibraries ?? []).map(
        (r: { getLibraryID: () => string }) => r.getLibraryID(),
      );
      const orParts = [
        ...(orgId ? [{ organizationID: { eq: orgId } }] : []),
        ...sharedLibraryIds.map((id: string) => ({ libraries: { matchPhrase: id } })),
      ];
      if (orParts.length > 0) {
        combinedFilter.and.push({ or: orParts });
        crossOrganization = true;
        const ext = combinedFilter as {
          organizationID?: string;
          libraryIds?: string[];
          combineOrganizationWithLibraries?: boolean;
        };
        ext.organizationID = orgId;
        ext.libraryIds = sharedLibraryIds;
        if (orgId && sharedLibraryIds.length > 0) {
          ext.combineOrganizationWithLibraries = true;
        }
      }
    } else if (filters.includes('portalLibraries')) {
      const orgId = store.currentOrganization?.id ?? '';
      const portalLibraryIds = store.currentPortal?.getLibraryIDs?.() ?? [];
      const orParts = [
        ...(orgId ? [{ organizationID: { eq: orgId } }] : []),
        ...portalLibraryIds.map((id: string) => ({ libraries: { matchPhrase: id } })),
      ];
      if (orParts.length > 0) {
        combinedFilter.and.push({ or: orParts });
        crossOrganization = true;
        const ext = combinedFilter as {
          organizationID?: string;
          libraryIds?: string[];
          combineOrganizationWithLibraries?: boolean;
        };
        if (orgId) ext.organizationID = orgId;
        ext.libraryIds = portalLibraryIds;
        if (orgId && portalLibraryIds.length > 0) {
          ext.combineOrganizationWithLibraries = true;
        }
      }
    }
  }

  if (filters.includes('membership') && searchMemberships.length > 0) {
    combinedFilter.and.push({
      or: searchMemberships.map((id) => ({ membershipID: { eq: id } })),
    });
  }

  if (filters.includes('pastFuture')) {
    if (searchPastFuture === 'Past') {
      combinedFilter.and.push({
        or: [
          { status: { eq: 'POST_MEETING' } },
          { status: { eq: 'CANCELLED' } },
        ],
      });
      sortValues = { field: 'startsAt', direction: 'desc' };
    } else if (searchPastFuture === 'Upcoming') {
      combinedFilter.and.push({
        or: [
          { status: { eq: 'PRE_MEETING' } },
          { status: { eq: 'OPEN' } },
          { status: { eq: 'ACTIVE' } },
        ],
      });
      sortValues = { field: 'startsAt', direction: 'asc' };
    }
  }

  if (filters.includes('activeStatusOnly') && activeStatusOnly) {
    combinedFilter.and.push({ status: { eq: 'ACTIVE' } });
  }

  if (filter && Object.keys(filter).length > 0) {
    if (filter === 'currentPortal') {
      combinedFilter.and.push({
        or: [
          { portalID: { eq: store.currentPortal?.id } },
          { portalID: { eq: store.currentPortal?.key } },
        ],
      });
    } else if (filter === 'currentOrganization') {
      combinedFilter.and.push({
        organizationID: { eq: store.currentOrganization?.id },
      });
      crossOrganization = true;
    } else if (filter === 'targetOrganization') {
      combinedFilter.and.push({
        targetOrganizationID: { eq: store.currentOrganization?.id },
      });
      crossOrganization = true;
    } else {
      combinedFilter.and.push(filter);
    }
  }

  return { combinedFilter, sortValues, crossOrganization };
}

export default buildCombinedFilter;
